import React, { useEffect, useState } from 'react';
import api from '../api/axios';
import { 
    Search, RefreshCw, MoreVertical, Eye, Lock, Unlock, Trash2, 
    Pill, Mail, MapPin, Phone, FileText, Hexagon
} from 'lucide-react';

const formatPhone = (value) => {
    if (!value) return '+992 ';
    const numbers = value.replace(/\D/g, '');
    const tjNum = numbers.startsWith('992') ? numbers.slice(3) : numbers;
    let res = '+992 ';
    if (tjNum.length > 0) res += tjNum.substring(0, 3);
    if (tjNum.length > 3) res += '-' + tjNum.substring(3, 5);
    if (tjNum.length > 5) res += '-' + tjNum.substring(5, 7);
    if (tjNum.length > 7) res += '-' + tjNum.substring(7, 9);
    return res;
};

const formatAddress = (addressStr) => {
    if (!addressStr) return "Адрес не указан";
    if (addressStr.includes('|||')) return addressStr.split('|||').filter(Boolean).join(', ');
    return addressStr;
};

const Pharmacies = () => {
    const [pharmacies, setPharmacies] = useState([]);
    const [prescriptions, setPrescriptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    
    const [searchQuery, setSearchQuery] = useState('');
    const [visibleCount, setVisibleCount] = useState(10);

    const [openMenuId, setOpenMenuId] = useState(null);
    const [selectedPharmacy, setSelectedPharmacy] = useState(null);

    useEffect(() => {
        setVisibleCount(10);
    }, [searchQuery]);

    useEffect(() => {
        const handleScroll = () => {
            if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 200) {
                setVisibleCount(prev => prev + 10);
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const handleClickOutside = () => setOpenMenuId(null);
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const [usersRes, prescRes] = await Promise.all([
                api.get('/users?role=pharmacy'),
                api.get('/prescriptions')
            ]);
            setPharmacies(usersRes.data);
            setPrescriptions(prescRes.data);
        } catch (error) {
            console.error(error);
            alert("Ошибка загрузки данных аптек");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const filteredPharmacies = pharmacies.filter(p => {
        const query = searchQuery.toLowerCase();
        return (
            (p.organization_name || '').toLowerCase().includes(query) ||
            (p.full_name || '').toLowerCase().includes(query) ||
            (p.email || '').toLowerCase().includes(query) ||
            (p.phone || '').toLowerCase().includes(query) ||
            (p.address || '').toLowerCase().includes(query)
        );
    });

    const visiblePharmacies = filteredPharmacies.slice(0, visibleCount);

    const toggleMenu = (e, id) => {
        e.stopPropagation();
        setOpenMenuId(openMenuId === id ? null : id);
    };

    const handleViewDetails = (e, pharmacy) => {
        e.stopPropagation();
        setSelectedPharmacy(pharmacy);
        setOpenMenuId(null);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Удалить эту аптеку?")) return;
        try {
            await api.delete(`/users/${id}`);
            setPharmacies(pharmacies.filter(p => p.id !== id));
        } catch (e) { alert("Ошибка удаления"); }
    };

    const handleToggleStatus = async (pharmacy) => {
        const newStatus = !pharmacy.is_active;
        try {
            await api.put(`/users/${pharmacy.id}`, { is_active: newStatus });
            setPharmacies(pharmacies.map(p => p.id === pharmacy.id ? { ...p, is_active: newStatus } : p));
        } catch (e) { alert("Ошибка изменения статуса"); }
    };

    const PharmacyDetailsModal = ({ pharmacy, onClose, allPrescriptions }) => {
        if (!pharmacy) return null;
        
        const issuedPrescriptions = allPrescriptions.filter(r => r.pharmacy_id === pharmacy.id).length;

        return (
            <div className="modal-overlay" onClick={onClose} style={overlayStyle}>
                <div className="modal-content" onClick={e => e.stopPropagation()} style={modalStyle}>
                    <div style={{ marginBottom: '24px' }}>
                        <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#111827', marginTop: '0', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Hexagon size={24} color="#10b981"/> Подробная информация
                        </h3>
                        <p style={{ color: '#6b7280' }}>{pharmacy.organization_name || 'Название не указано'}</p>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '30px', background: '#f8fafc', padding: '20px', borderRadius: '12px' }}>
                        <div><p style={detailLabel}>Фармацевт:</p><p style={detailValue}>{pharmacy.full_name}</p></div>
                        <div><p style={detailLabel}>Номер лицензии:</p><p style={detailValue}>{pharmacy.license_number || 'Не указан'}</p></div>
                        <div><p style={detailLabel}>Email:</p><p style={detailValue}>{pharmacy.email}</p></div>
                        <div><p style={detailLabel}>Телефон:</p><p style={detailValue}>{formatPhone(pharmacy.phone)}</p></div>
                        <div style={{gridColumn: '1 / -1'}}><p style={detailLabel}>Адрес:</p><p style={detailValue}>{formatAddress(pharmacy.address)}</p></div>
                    </div>

                    <div style={{ marginBottom: '30px' }}>
                        <p style={detailLabel}>Статистика и Статус:</p>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '8px' }}>
                            <div style={{ background: '#ecfdf5', padding: '16px', borderRadius: '12px', border: '1px solid #d1fae5' }}>
                                <p style={{ color: '#047857', fontSize: '0.9rem', marginBottom: '4px' }}>Рецептов обработано</p>
                                <p style={{ color: '#059669', fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>{issuedPrescriptions}</p>
                            </div>
                            <div style={{ background: pharmacy.is_active ? '#eff6ff' : '#fef2f2', padding: '16px', borderRadius: '12px', border: pharmacy.is_active ? '1px solid #dbeafe' : '1px solid #fee2e2', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '4px' }}>Текущий статус</p>
                                <span style={{
                                    display: 'inline-block', padding: '4px 12px', borderRadius: '20px', fontSize: '0.9rem', fontWeight: '600', width: 'fit-content',
                                    background: pharmacy.is_active ? '#0f172a' : '#ef4444', color: 'white'
                                }}>
                                    {pharmacy.is_active ? 'Активна' : 'Заблокирована'}
                                </span>
                            </div>
                        </div>
                    </div>
                    <button onClick={onClose} style={closeBtnStyle}>Закрыть</button>
                </div>
            </div>
        );
    };

    return (
        <div className="content-wrapper">
            <style>{`
                .animate-spin { animation: spin 1s linear infinite; }
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                .pharmacy-custom-card {
                    background: white; border: 1px solid #e5e7eb; border-radius: 12px; padding: 20px;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.05); margin-bottom: 16px; position: relative;
                }
                .p-icon-circle {
                    width: 40px; height: 40px; border-radius: 50%; background: #d1fae5; color: #10b981;
                    display: flex; align-items: center; justify-content: center;
                }
                .p-title-row { display: flex; align-items: center; gap: 12px; margin-bottom: 4px; }
                .p-title { font-size: 1.1rem; font-weight: 600; color: #111827; margin: 0; }
                .p-badge { background: #0f172a; color: white; padding: 2px 10px; border-radius: 6px; font-size: 0.75rem; font-weight: 500; }
                .p-badge-blocked { background: #fee2e2; color: #991b1b; padding: 2px 10px; border-radius: 6px; font-size: 0.75rem; font-weight: 500; }
                .p-subtitle { font-size: 0.85rem; color: #6b7280; margin: 0 0 20px 0; }
                .p-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 16px; }
                .p-info-item { display: flex; align-items: center; gap: 8px; font-size: 0.9rem; color: #4b5563; }
                .p-footer { font-size: 0.8rem; color: #9ca3af; margin: 0; }
            `}</style>
            
            <div className="card doc-controls-card" style={{margin: '0'}}>
                <div className="card-header doc-header-flex">
                    <div className="doc-title-block">
                        <h4 className="card-title text-green" style={{display:'flex', alignItems:'center', gap:'10px'}}>
                            <Hexagon size={24} color="#10b981" /> Управление аптеками
                        </h4>
                        <p className="card-subtitle">Всего аптек: {pharmacies.length} • Активных: {pharmacies.filter(p => p.is_active).length}</p>
                    </div>
                    <button className="flex-none btn-outline btn-sm" onClick={fetchData} style={{display:'flex', gap:'8px', alignItems:'center'}}>
                        <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} /> Обновить
                    </button>
                </div>
                
                <div className="card-body doc-filter-body">
                    <div className="search-wrapper" style={{width: '100%'}}>
                        <Search className="search-icon" size={18} />
                        <input 
                            type="text" className="search-input" 
                            placeholder="Поиск по названию, фармацевту, email..." 
                            value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            <div>
                {visiblePharmacies.length === 0 && !isLoading && (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>Ничего не найдено</div>
                )}
                
                {visiblePharmacies.map(p => {
                    const issuedCount = prescriptions.filter(r => r.pharmacy_id === p.id).length;

                    return (
                        <div key={p.id} className="pharmacy-custom-card">
                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                                <div style={{display: 'flex', gap: '16px'}}>
                                    <div className="p-icon-circle"><Pill size={20}/></div>
                                    <div>
                                        <div className="p-title-row">
                                            <h3 className="p-title">{p.organization_name || 'Аптека без названия'}</h3>
                                            <span className={p.is_active ? "p-badge" : "p-badge-blocked"}>
                                                {p.is_active ? 'Активна' : 'Заблокирована'}
                                            </span>
                                        </div>
                                        <p className="p-subtitle">Фармацевт: {p.full_name}</p>
                                    </div>
                                </div>
                                
                                <div style={{position: 'relative'}}>
                                    <button onClick={(e) => toggleMenu(e, p.id)} style={{background:'none', border:'none', cursor:'pointer', padding:'4px', color:'#6b7280'}}>
                                        <MoreVertical size={20} />
                                    </button>
                                    {openMenuId === p.id && (
                                        <div style={dropdownStyle}>
                                            <button onClick={(e) => handleViewDetails(e, p)} style={menuItemStyle}>
                                                <Eye size={16} /> Подробная информация
                                            </button>
                                            <button onClick={() => handleToggleStatus(p)} style={{...menuItemStyle, color: p.is_active ? '#f59e0b' : '#10b981'}}>
                                                {p.is_active ? <><Lock size={16}/> Заблокировать</> : <><Unlock size={16}/> Разблокировать</>}
                                            </button>
                                            <button onClick={() => handleDelete(p.id)} style={{...menuItemStyle, color: '#ef4444', borderTop: '1px solid #f3f4f6'}}>
                                                <Trash2 size={16} /> Удалить
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="p-grid" style={{marginLeft: '56px'}}>
                                <div>
                                    <div className="p-info-item" style={{marginBottom: '10px'}}><Mail size={16} color="#9ca3af"/> {p.email}</div>
                                    <div className="p-info-item"><FileText size={16} color="#9ca3af"/> Выдано рецептов: {issuedCount}</div>
                                </div>
                                <div>
                                    <div className="p-info-item" style={{marginBottom: '10px'}}><MapPin size={16} color="#9ca3af"/> {formatAddress(p.address)}</div>
                                    {p.phone && <div className="p-info-item"><Phone size={16} color="#9ca3af"/> {formatPhone(p.phone)}</div>}
                                </div>
                            </div>

                            <p className="p-footer" style={{marginLeft: '56px', marginTop: '16px'}}>
                                Зарегистрирована: {new Date(p.created_at).toLocaleDateString()}
                            </p>
                        </div>
                    );
                })}
            </div>
            
            {!isLoading && visiblePharmacies.length < filteredPharmacies.length && (
                <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                    Прокрутите вниз, чтобы загрузить еще...
                </div>
            )}

            <PharmacyDetailsModal 
                pharmacy={selectedPharmacy} 
                onClose={() => setSelectedPharmacy(null)} 
                allPrescriptions={prescriptions} 
            />
        </div>
    );
};

const dropdownStyle = { position: 'absolute', right: 0, top: '30px', width: '230px', backgroundColor: 'white', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', borderRadius: '8px', border: '1px solid #e5e7eb', zIndex: 50, overflow: 'hidden' };
const menuItemStyle = { display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '10px 16px', border: 'none', background: 'white', cursor: 'pointer', textAlign: 'left', fontSize: '0.9rem', color: '#374151', transition: 'background 0.2s' };
const overlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(2px)' };
const modalStyle = { backgroundColor: 'white', borderRadius: '16px', width: '600px', maxWidth: '95%', padding: '32px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', position: 'relative' };
const detailLabel = { fontSize: '0.85rem', color: '#6b7280', marginBottom: '4px', margin: 0 };
const detailValue = { fontSize: '1rem', fontWeight: '600', color: '#111827', margin: 0 };
const closeBtnStyle = { width: '100%', padding: '12px', backgroundColor: '#0f172a', color: 'white', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: '500', cursor: 'pointer' };

export default Pharmacies;